import { useState } from "react";
import { useFetch } from "../../hooks/useFetch";

type RateLimitEntry = {
	group: string;
	dimension: string;
	identity: string;
	currentCount: number;
	maxLimit: number;
	throttledCount: number;
	windowReset: number;
};

type RateLimitsResponse = {
	entries: RateLimitEntry[];
	cursor: string | null;
};

const GROUPS = [
	"auth-signin",
	"auth-signup",
	"auth-password-reset",
	"auth-default",
	"tenant-api",
	"client-logs",
	"tenant-api-key",
];

const DIMENSIONS = ["ip", "tenant", "apikey", "user"];

function formatResetTime(epochSeconds: number): string {
	const now = Math.floor(Date.now() / 1000);
	const diff = epochSeconds - now;
	if (diff <= 0) return "expired";
	if (diff < 60) return `in ${diff}s`;
	return `in ${Math.ceil(diff / 60)}m`;
}

export function AdminRateLimitsPage() {
	const [group, setGroup] = useState("");
	const [dimension, setDimension] = useState("");
	const [cursorStack, setCursorStack] = useState<string[]>([]);
	const [currentCursor, setCurrentCursor] = useState("");

	const params = new URLSearchParams();
	if (group) params.set("group", group);
	if (dimension) params.set("dimension", dimension);
	if (currentCursor) params.set("cursor", currentCursor);

	const { data, loading, error } = useFetch<RateLimitsResponse>(
		`/api/admin/rate-limits?${params.toString()}`,
	);

	function handleFilterChange() {
		setCursorStack([]);
		setCurrentCursor("");
	}

	function handleNext() {
		if (data?.cursor) {
			setCursorStack((s) => [...s, currentCursor]);
			setCurrentCursor(data.cursor);
		}
	}

	function handlePrevious() {
		setCursorStack((s) => {
			const next = [...s];
			const prev = next.pop() ?? "";
			setCurrentCursor(prev);
			return next;
		});
	}

	return (
		<div>
			<h1 className="text-2xl font-bold text-gray-900 mb-4">Rate Limits</h1>

			<div className="flex flex-wrap gap-3 mb-4 items-end">
				<div>
					<label
						htmlFor="filter-group"
						className="block text-xs text-gray-600 mb-1"
					>
						Group
					</label>
					<select
						id="filter-group"
						value={group}
						onChange={(e) => {
							setGroup(e.target.value);
							handleFilterChange();
						}}
						className="text-sm border border-gray-300 rounded px-2 py-1"
					>
						<option value="">All</option>
						{GROUPS.map((g) => (
							<option key={g} value={g}>
								{g}
							</option>
						))}
					</select>
				</div>
				<div>
					<label
						htmlFor="filter-dimension"
						className="block text-xs text-gray-600 mb-1"
					>
						Dimension
					</label>
					<select
						id="filter-dimension"
						value={dimension}
						onChange={(e) => {
							setDimension(e.target.value);
							handleFilterChange();
						}}
						className="text-sm border border-gray-300 rounded px-2 py-1"
					>
						<option value="">All</option>
						{DIMENSIONS.map((d) => (
							<option key={d} value={d}>
								{d}
							</option>
						))}
					</select>
				</div>
			</div>

			{error && <p className="text-red-600 text-sm mb-3">{error}</p>}

			{loading && !data && <p className="text-gray-500 text-sm">Loading…</p>}

			{data && (
				<>
					<div className="overflow-x-auto">
						<table className="w-full text-sm text-left">
							<thead className="bg-gray-100 text-gray-700">
								<tr>
									<th className="px-4 py-2">Identity</th>
									<th className="px-4 py-2">Group</th>
									<th className="px-4 py-2">Usage</th>
									<th className="px-4 py-2">Throttled</th>
									<th className="px-4 py-2">Resets At</th>
								</tr>
							</thead>
							<tbody>
								{data.entries.map((entry) => {
									const usageRatio =
										entry.maxLimit > 0
											? entry.currentCount / entry.maxLimit
											: 0;
									return (
										<tr
											key={`${entry.dimension}:${entry.identity}:${entry.group}:${entry.windowReset}`}
											className="border-b border-gray-200"
										>
											<td className="px-4 py-2 font-mono text-xs">
												{entry.identity}
											</td>
											<td className="px-4 py-2">
												<span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
													{entry.group}
												</span>
												<span className="ml-1 text-xs text-gray-400">
													{entry.dimension}
												</span>
											</td>
											<td className="px-4 py-2">
												<span
													className={
														usageRatio > 0.8 ? "text-red-600 font-semibold" : ""
													}
												>
													{entry.currentCount} / {entry.maxLimit}
												</span>
											</td>
											<td className="px-4 py-2">
												{entry.throttledCount > 0 ? (
													<span className="text-red-600 font-medium">
														{entry.throttledCount}
													</span>
												) : (
													<span className="text-gray-400">0</span>
												)}
											</td>
											<td className="px-4 py-2 text-gray-600">
												{formatResetTime(entry.windowReset)}
											</td>
										</tr>
									);
								})}
								{data.entries.length === 0 && (
									<tr>
										<td
											colSpan={5}
											className="px-4 py-8 text-center text-gray-500"
										>
											No active rate limit entries.
										</td>
									</tr>
								)}
							</tbody>
						</table>
					</div>

					<div className="flex items-center justify-between mt-4">
						<button
							type="button"
							disabled={cursorStack.length === 0}
							onClick={handlePrevious}
							className="text-sm px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
						>
							Previous
						</button>
						<button
							type="button"
							disabled={!data.cursor}
							onClick={handleNext}
							className="text-sm px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
						>
							Next
						</button>
					</div>
				</>
			)}
		</div>
	);
}
